"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  quote: "Chef Jess transformed our dinner into an unforgettable experience. The flavors were incredible and the atmosphere was perfect!",
  name: "Maria Rodriguez",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_maria_portrait.png"
}, {
  id: 2,
  quote: "The cooking class was so much fun! I learned techniques I'll use forever. Chef Jess made everything approachable and exciting.",
  name: "David Chen",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_david_portrait.png"
}, {
  id: 3,
  quote: "Pass the Plate brought our community together. Every dish told a story, and meeting new friends over food was magical.",
  name: "Sarah Johnson",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_sarah_portrait.png"
}, {
  id: 4,
  quote: "I've attended three supper clubs now and each one is better than the last. The creativity and care in every course is remarkable.",
  name: "Michael Park",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_michael_portrait.png"
}, {
  id: 5,
  quote: "Create the Plate gave me the confidence to cook for my family. Chef Jess's teaching style is warm, patient, and inspiring.",
  name: "Emily Santos",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_emily_portrait.png"
}, {
  id: 6,
  quote: "The intimate setting and curated menu made for the most memorable dining experience. Can't wait for the next one!",
  name: "James Wilson",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/testimonial_james_portrait.png"
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [fadeState, setFadeState] = useState<"in" | "out">("in");
  const visibleCount = 3;
  const autoRotateDelay = 5000;
  const fadeTransitionDuration = 300;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const totalSlides = Math.ceil(testimonials.length / visibleCount);
  const nextSlide = () => {
    setFadeState("out");
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % totalSlides);
      setFadeState("in");
    }, fadeTransitionDuration);
  };
  const prevSlide = () => {
    setFadeState("out");
    setTimeout(() => {
      setCurrentIndex(prev => (prev - 1 + totalSlides) % totalSlides);
      setFadeState("in");
    }, fadeTransitionDuration);
  };
  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = setInterval(() => {
      nextSlide();
    }, autoRotateDelay);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPaused, currentIndex]);
  const startIndex = currentIndex * visibleCount;
  const currentTestimonials = testimonials.slice(startIndex, startIndex + visibleCount);
  return <section id="testimonials" ref={ref} className={`py-20 px-4 bg-background transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            What Our Guests Say
          </h2>
          
        </div>

        <div className="relative">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-opacity duration-[var(--duration-medium)]`} style={{
          opacity: fadeState === "in" ? 1 : 0
        }} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            {currentTestimonials.map((testimonial, i) => <Card key={testimonial.id} data-index={i} className="hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] shadow-soft hover:shadow-medium bg-background border border-border rounded-lg px-6">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center gap-4">
                    <SafeHydrate><Avatar className="size-16 border-2 border-primary/20">
                      <AvatarImage src={testimonial.image} alt={testimonial.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {testimonial.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>

                    <blockquote className="text-foreground/90 leading-relaxed min-h-[120px] flex items-center">
                      "{testimonial.quote}"
                    </blockquote>

                    <div className="font-semibold text-foreground hover:underline underline-offset-4 transition-all duration-[var(--duration-fast)]" style={{
                  cursor: "default"
                }}>
                      {testimonial.name}
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <div className="flex justify-center items-center gap-4 mt-10">
            <Button variant="outline" size="icon" onClick={prevSlide} className="size-10 rounded-full hover:bg-primary/10 hover:border-primary/40 transition-all duration-[var(--duration-medium)] hover:scale-110" aria-label="Previous testimonials">
              <ChevronLeft className="size-5" />
            </Button>

            <div className="flex gap-2">
              {Array.from({
              length: totalSlides
            }).map((_, idx) => <Button key={idx} variant="ghost" size="icon" onClick={() => {
              setFadeState("out");
              setTimeout(() => {
                setCurrentIndex(idx);
                setFadeState("in");
              }, fadeTransitionDuration);
            }} className={`size-2.5 rounded-full p-0 min-w-0 h-auto transition-all duration-[var(--duration-medium)] ${idx === currentIndex ? "bg-primary w-8" : "bg-muted hover:bg-primary/50"}`} aria-label={`Go to slide ${idx + 1}`} />)}
            </div>

            <Button variant="outline" size="icon" onClick={nextSlide} className="size-10 rounded-full hover:bg-primary/10 hover:border-primary/40 transition-all duration-[var(--duration-medium)] hover:scale-110" aria-label="Next testimonials">
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}